import { useState, useCallback } from "react";
import { useFirebaseAuth } from "./FirebaseAuth";

interface FetchState<T> {
  data: T | null;
  error: string | null;
  statusCode: number | null;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

interface FetchResponse<T> {
  data: T | null;
  error: string | null;
  statusCode: number | null;
}

interface FetchOptions {
  url: string;
  method?: string;
  initialData?: any;
}

interface FetchResult<T> extends FetchState<T> {
  callAPI: (payload?: any) => Promise<FetchResponse<T>>;
  reset: () => void;
}

export function useAuthenticatedFetchV2<T = any>({
  url,
  method = "GET",
  initialData = null,
}: FetchOptions): FetchResult<T> {
  const user = useFirebaseAuth();
  const [state, setState] = useState<FetchState<T>>({
    data: initialData,
    error: null,
    statusCode: null,
    isLoading: false,
    isError: false,
    isSuccess: false,
  });

  const reset = useCallback(() => {
    setState({
      data: initialData,
      error: null,
      statusCode: null,
      isLoading: false,
      isError: false,
      isSuccess: false,
    });
  }, [initialData]);

  const callAPI = useCallback(
    async (payload?: any): Promise<FetchResponse<T>> => {
      if (!user) {
        const errorResponse = {
          data: null,
          error: "You are not authorized",
          statusCode: 401,
        };
        setState({
          ...errorResponse,
          isLoading: false,
          isError: true,
          isSuccess: false,
        });
        return errorResponse;
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const token = await user.getIdToken();
        const requestOptions: RequestInit = {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        };

        if (method !== "GET" && payload) {
          requestOptions.body =
            typeof payload === "string" ? payload : JSON.stringify(payload);
        }

        const response = await fetch(url, requestOptions);
        const result = await response.json();

        if (response.ok) {
          const responseData = {
            data: result,
            error: null,
            statusCode: response.status,
          };

          setState({
            ...responseData,
            isLoading: false,
            isError: false,
            isSuccess: true,
          });

          return responseData;
        } else {
          const responseData = {
            data: null,
            error: result.error || "Request failed",
            statusCode: response.status,
          };

          setState({
            ...responseData,
            isLoading: false,
            isError: true,
            isSuccess: false,
          });

          return responseData;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        const responseData = {
          data: null,
          error: errorMessage,
          statusCode: null,
        };

        setState({
          ...responseData,
          isLoading: false,
          isError: true,
          isSuccess: false,
        });

        return responseData;
      }
    },
    [user, method, url]
  );

  return {
    ...state,
    callAPI,
    reset,
  };
}
